import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useBluelagoon } from "@bluelagoon";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { track } from "@crm/analytics";
import { BlueprintWorkflowModal } from "../../Blueprints/BlueprintWorkflowModal";
import RecommendedBlueprintCard, {
    RecommendedBlueprintSkeleton,
} from "./RecommendedBlueprintCard";
import AiRecommendedCard, { AiRecommendedSkeleton } from "./AiRecommendedCard";
import UserTypeDashboards from "./UserTypeDashboards";

export default function RecommendedBlueprints({ userType, onPromptClick, user }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { interfaceStyle } = useAuth();
    const [blueprints, setBlueprints] = useState([]);
    const [aiRecommended, setAiRecommended] = useState(null);
    const [aiLoading, setAiLoading] = useState(false);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const [selectedBlueprint, setSelectedBlueprint] = useState(null);
    const [showModal, setShowModal] = useState(false);

    const { call } = useBluelagoon({ method: "recommendBlueprints" });
    const callRef = useRef(call);
    callRef.current = call;

    useEffect(() => {
        const load = async () => {
            try {
                setLoading(true);
                const res = await callRef.current();
                const list = res?.data?.blueprints || [];

                // Check localStorage for AI recommendation
                let aiData = null;
                try {
                    const stored = localStorage.getItem("ai_recommended_blueprint");
                    if (stored) {
                        aiData = JSON.parse(stored);
                        localStorage.removeItem("ai_recommended_blueprint");
                    }
                } catch {}

                // Fallback: check user.onboardingMetadata
                if (!aiData?.blueprint && user?.onboardingMetadata?.aiRecommendedBlueprintId) {
                    const match = list.find(
                        (bp) => bp.id === user.onboardingMetadata.aiRecommendedBlueprintId
                    );
                    if (match) {
                        aiData = { blueprint: match, reason: user.onboardingMetadata.aiRecommendReason };
                    }
                }

                if (aiData?.blueprint) {
                    setAiRecommended(aiData);
                    const filtered = list.filter((bp) => bp.id !== aiData.blueprint.id);
                    setBlueprints(filtered.slice(0, 4));
                } else {
                    setBlueprints(list.slice(0, 4));
                    // If user just completed onboarding, AI recommend is in-flight
                    if (user?.onboardingMetadata?.buildingGoal && !user?.onboardingMetadata?.aiRecommendedBlueprintId) {
                        setAiLoading(true);
                    }
                }

                if (list.length === 0) setFailed(true);
            } catch {
                setFailed(true);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [user?.termsAccepted?.isValid]);

    // Listen for late-arriving AI recommendation from TermsAcceptance
    useEffect(() => {
        const handleAiReady = () => {
            try {
                const stored = localStorage.getItem("ai_recommended_blueprint");
                if (stored) {
                    const aiData = JSON.parse(stored);
                    localStorage.removeItem("ai_recommended_blueprint");
                    if (aiData?.blueprint) {
                        setAiRecommended(aiData);
                        setAiLoading(false);
                        setFailed(false);
                        setBlueprints((prev) =>
                            prev.filter((bp) => bp.id !== aiData.blueprint.id).slice(0, 4)
                        );
                    }
                }
            } catch {}
        };
        window.addEventListener("ai-blueprint-ready", handleAiReady);
        return () => window.removeEventListener("ai-blueprint-ready", handleAiReady);
    }, []);

    // Timeout: stop showing AI skeleton after 8s
    useEffect(() => {
        if (!aiLoading) return;
        const timer = setTimeout(() => setAiLoading(false), 8000);
        return () => clearTimeout(timer);
    }, [aiLoading]);

    const handleCardClick = (blueprint) => {
        setSelectedBlueprint(blueprint);
        setShowModal(true);
    };

    const handleWorkflowCreated = (workflowId, blueprint) => {
        track("Created Workflow from Blueprint", {
            blueprintId: blueprint.id,
            blueprintName: blueprint.name,
            source: "chat_page",
        });
        toast.success(t("blueprints.workflowCreated", { name: blueprint.name }));
        setTimeout(() => {
            navigate(
                interfaceStyle === "expert"
                    ? `/workflows/${workflowId}`
                    : `/chat/${workflowId}/chat`
            );
        }, 100);
    };

    if (!loading && failed) {
        return (
            <UserTypeDashboards
                userType={userType}
                onPromptClick={onPromptClick}
                user={user}
            />
        );
    }

    return (
        <>
            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[...Array(4)].map((_, i) => (
                        <RecommendedBlueprintSkeleton key={i} />
                    ))}
                </div>
            ) : (
                <>
                    {aiRecommended?.blueprint ? (
                        <AiRecommendedCard
                            blueprint={aiRecommended.blueprint}
                            reason={aiRecommended.reason}
                            onClick={handleCardClick}
                        />
                    ) : aiLoading ? (
                        <AiRecommendedSkeleton />
                    ) : null}
                    <div className={`grid grid-cols-1 md:grid-cols-2 gap-4${aiRecommended?.blueprint ? " mt-4" : ""}`}>
                        {blueprints.map((bp) => (
                            <RecommendedBlueprintCard
                                key={bp.id}
                                blueprint={bp}
                                onClick={handleCardClick}
                            />
                        ))}
                    </div>
                    <div className="mt-6 text-center">
                        <button
                            onClick={() => navigate("/blueprints")}
                            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                        >
                            {t("chatPage.browseTemplates", {
                                defaultValue: "Browse all templates",
                            })}
                        </button>
                    </div>
                </>
            )}
            <BlueprintWorkflowModal
                isOpen={showModal}
                onClose={() => {
                    setShowModal(false);
                    setSelectedBlueprint(null);
                }}
                onWorkflowCreated={handleWorkflowCreated}
                blueprint={selectedBlueprint}
            />
        </>
    );
}
